/*
 * dailyDrop — once-a-calendar-day cosmetic-gift scheduler.
 *
 * Storage lives in its own localStorage key (`fernhollow-daily-drop`) and is
 * intentionally separate from the main `SaveData` schema in `state/save.ts`.
 * That way the daily-drop feature can evolve / be wiped / be reset for testing
 * without ever touching the canonical save.
 *
 * Date handling uses the player's local timezone — players who open the game
 * "in the evening" expect tomorrow's drop to land in their tomorrow morning,
 * not at UTC midnight. `toLocaleDateString('sv-SE')` is the standard trick to
 * get a `YYYY-MM-DD` string in local time without pulling in a date library.
 *
 * No-repeat-within-7-days rule: we keep the most recent 7 claimed ids and
 * exclude them from the candidate pool. If for some reason the pool is fully
 * covered by the recent history (it can't be today — 18 drops vs 7 slots —
 * but a future trim could change that) we fall back to allowing any drop so
 * the player still gets something.
 */

import { DAILY_DROPS, type DailyDrop } from '@/content/dailyDrops';
import { showDropToast } from '@/ui/drop-toast';

const STORAGE_KEY = 'fernhollow-daily-drop';

/** Persisted shape. Keep this small and obvious — it's user-inspectable in devtools. */
interface DailyDropSave {
  /** YYYY-MM-DD in the player's local timezone, or '' if never claimed. */
  lastDropDate: string;
  /** Most recent claimed ids, oldest → newest, trimmed to {@link HISTORY_LIMIT}. */
  claimedIds: string[];
}

/** How many recent claims to remember for the no-repeat rule. */
const HISTORY_LIMIT = 7;

/** YYYY-MM-DD string in the player's local timezone. */
function todayLocal(): string {
  // 'sv-SE' (Swedish) locale formats as YYYY-MM-DD — the cleanest way to get
  // a sortable local-date string without manually padding month/day.
  return new Date().toLocaleDateString('sv-SE');
}

function loadState(): DailyDropSave {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { lastDropDate: '', claimedIds: [] };
    const parsed = JSON.parse(raw) as Partial<DailyDropSave>;
    const lastDropDate =
      typeof parsed.lastDropDate === 'string' ? parsed.lastDropDate : '';
    const claimedIds = Array.isArray(parsed.claimedIds)
      ? parsed.claimedIds.filter((s): s is string => typeof s === 'string')
      : [];
    return { lastDropDate, claimedIds };
  } catch {
    // Corrupt entry, private browsing, or storage disabled — treat as fresh.
    return { lastDropDate: '', claimedIds: [] };
  }
}

function saveState(state: DailyDropSave): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage unavailable — silently drop. The UI side already ran; missing
    // persistence just means tomorrow's check might re-show today's drop,
    // which is a benign failure mode for a flavor feature.
  }
}

/**
 * Pick a drop the player hasn't seen in their most-recent {@link HISTORY_LIMIT}
 * claims. Falls back to the full pool if every drop has been seen recently
 * (defensive — shouldn't happen with the current 18-item pool).
 */
function pickDrop(recentIds: readonly string[]): DailyDrop | null {
  if (DAILY_DROPS.length === 0) return null;
  const recentSet = new Set(recentIds);
  const candidates = DAILY_DROPS.filter((d) => !recentSet.has(d.id));
  const pool = candidates.length > 0 ? candidates : DAILY_DROPS;
  const idx = Math.floor(Math.random() * pool.length);
  // `pool` is non-empty here; the `?? null` is a TS-narrowing safety net.
  return pool[idx] ?? null;
}

/**
 * Public entry point. Call once on boot.
 *
 * Idempotent for a given calendar date: if `lastDropDate` already equals
 * today, this is a no-op. Safe to call repeatedly (e.g. on hot-reload).
 */
export function checkAndShowDailyDrop(): void {
  const state = loadState();
  const today = todayLocal();
  if (state.lastDropDate === today) return;

  const drop = pickDrop(state.claimedIds);
  if (!drop) return;

  const nextClaimed = [...state.claimedIds, drop.id].slice(-HISTORY_LIMIT);
  saveState({ lastDropDate: today, claimedIds: nextClaimed });

  showDropToast(drop);
}
